import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: {
      finalY: number;
    };
  }
}

/**
 * Common utilities for exporting data to CSV and PDF formats
 */

/**
 * Convert array of objects to CSV format
 */
export const arrayToCSV = (data: any[], headers?: string[]): string => {
  if (data.length === 0) return '';

  // Use provided headers or extract from first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.map(header => `"${header}"`).join(',');
  
  // Create data rows
  const dataRows = data.map(row => {
    return csvHeaders.map(header => {
      const value = row[header];
      // Handle null/undefined values
      if (value === null || value === undefined) return '""';
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [headerRow, ...dataRows].join('\n');
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Download PDF file
 */
export const downloadPDF = (pdf: jsPDF, filename: string): void => {
  pdf.save(filename);
};

/**
 * Create a new PDF with standard formatting
 */
export const createPDF = (title: string, orientation: 'portrait' | 'landscape' = 'portrait'): jsPDF => {
  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4'
  });

  // Add title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, 20, 20);
  
  // Add timestamp
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const timestamp = new Date().toLocaleString();
  pdf.text(`Generated on: ${timestamp}`, 20, 30);
  
  return pdf;
};

/**
 * Add table to PDF using autoTable
 */
export const addTableToPDF = (
  pdf: jsPDF, 
  headers: string[], 
  data: any[][], 
  startY: number = 40,
  options: any = {}
): number => {
  const defaultOptions = {
    startY,
    head: [headers],
    body: data,
    theme: 'grid',
    headStyles: { 
      fillColor: [66, 139, 202],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: { 
      fillColor: [245, 245, 245] 
    },
    styles: { 
      fontSize: 8,
      cellPadding: 2
    },
    margin: { top: 20, right: 20, bottom: 20, left: 20 },
    ...options
  };

  pdf.autoTable(defaultOptions);
  
  // Return the Y position after the table
  return (pdf as any).lastAutoTable.finalY || startY + 20;
};

/**
 * Add page break if needed
 */
export const addPageBreakIfNeeded = (pdf: jsPDF, currentY: number, requiredSpace: number = 40): number => {
  const pageHeight = pdf.internal.pageSize.height;
  
  if (currentY + requiredSpace > pageHeight - 20) {
    pdf.addPage();
    return 20; // Return top margin for new page
  }
  
  return currentY;
};

/**
 * Format time minutes for display (e.g., "2h 30m")
 */
export const formatTimeForDisplay = (minutes: number): string => {
  if (minutes === 0 || minutes === null || minutes === undefined) return '-';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format decimal hours for display (e.g., "2.5h")
 */
export const formatDecimalHoursForDisplay = (hours: number): string => {
  if (hours === 0 || hours === null || hours === undefined) return '-';
  return `${hours.toFixed(1)}h`;
};

/**
 * Format percentage for display
 */
export const formatPercentageForDisplay = (value: number): string => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
};

/**
 * Format score for display
 */
export const formatScoreForDisplay = (value: number, decimals: number = 1): string => {
  if (value === null || value === undefined) return '-';
  return value.toFixed(decimals);
};

/**
 * Generate filename with timestamp
 */
export const generateFilename = (baseName: string, extension: string, includeTimestamp: boolean = true): string => {
  const sanitizedBaseName = baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  
  if (includeTimestamp) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    return `${sanitizedBaseName}_${timestamp}.${extension}`;
  }
  
  return `${sanitizedBaseName}.${extension}`;
};

/**
 * Extract data mode label for filenames and titles
 */
export const getDataModeLabel = (dataMode: string): string => {
  switch (dataMode) {
    case 'basic': return 'Basic';
    case 'my-team': return 'My_Team';
    case 'central-ps': return 'Central_PS';
    default: return 'Unknown';
  }
};

/**
 * Extract period label for filenames and titles
 */
export const getPeriodLabel = (reportingPeriod: string): string => {
  switch (reportingPeriod) {
    case 'weekly': return 'Weekly';
    case '4weekly': return '4_Weekly';
    default: return 'Unknown_Period';
  }
};
